/**
 * v-fit-text: keep an element's text on ONE line by shrinking its font size until it fits
 * its container, and growing it back (up to its CSS size) when room returns.
 *
 * The element's own CSS font-size is the ceiling (it may be a clamp() on container units);
 * the directive only ever lowers it. Re-runs on element/parent resize (ResizeObserver), on
 * the 'dashboard-widget-resize' window event the grid dispatches, and on updates.
 *
 * Usage: <p v-fit-text>long text</p>   or   <p v-fit-text="{ min: 9, short: 'fallback text' }">
 * `short`: alternative text swapped in when even the minimum size overflows (the full text
 * should stay reachable elsewhere, e.g. a title attribute).
 */
const MIN_PX = 8;

function fit(el, opts) {
  const min = opts && opts.min ? Number(opts.min) : MIN_PX;
  el.style.whiteSpace = 'nowrap';
  if (el.__fitTextFull !== undefined && el.textContent !== el.__fitTextFull && el.textContent === el.__fitTextShortApplied) {
    el.textContent = el.__fitTextFull;          // measure the full text first on every pass
  }
  el.__fitTextFull = el.textContent;
  el.__fitTextShortApplied = null;
  el.style.fontSize = '';                       // back to the CSS ceiling, then shrink as needed
  const ceiling = parseFloat(getComputedStyle(el).fontSize);
  if (!ceiling || el.clientWidth === 0) return;
  let size = ceiling;
  // Proportional first guess, then settle with small steps (sub-pixel rounding).
  if (el.scrollWidth > el.clientWidth) {
    size = Math.max(min, Math.floor((ceiling * el.clientWidth) / el.scrollWidth * 10) / 10);
    el.style.fontSize = size + 'px';
  }
  let guard = 40;
  while (el.scrollWidth > el.clientWidth && size > min && guard-- > 0) {
    size = Math.max(min, size - 0.5);
    el.style.fontSize = size + 'px';
  }
  // Still too long at the floor: swap in the short form and fit that from the ceiling.
  if (el.scrollWidth > el.clientWidth && opts && opts.short && opts.short !== el.textContent && el.children.length === 0) {
    el.textContent = opts.short;
    el.__fitTextShortApplied = opts.short;
    el.style.fontSize = '';
    const top = parseFloat(getComputedStyle(el).fontSize) || ceiling;
    let s2 = top;
    if (el.scrollWidth > el.clientWidth) {
      s2 = Math.max(min, Math.floor((top * el.clientWidth) / el.scrollWidth * 10) / 10);
      el.style.fontSize = s2 + 'px';
    }
    let g2 = 40;
    while (el.scrollWidth > el.clientWidth && s2 > min && g2-- > 0) {
      s2 = Math.max(min, s2 - 0.5);
      el.style.fontSize = s2 + 'px';
    }
  }
}

function schedule(el) {
  if (el.__fitTextRaf) cancelAnimationFrame(el.__fitTextRaf);
  el.__fitTextRaf = requestAnimationFrame(() => {
    el.__fitTextRaf = null;
    fit(el, el.__fitTextOpts);
  });
}

export default {
  inserted(el, binding) {
    el.__fitTextOpts = binding.value || {};
    el.__fitTextResize = () => schedule(el);
    window.addEventListener('dashboard-widget-resize', el.__fitTextResize);
    window.addEventListener('resize', el.__fitTextResize);
    if (typeof ResizeObserver !== 'undefined') {
      el.__fitTextObserver = new ResizeObserver(el.__fitTextResize);
      el.__fitTextObserver.observe(el.parentElement || el);
    }
    schedule(el);
  },
  componentUpdated(el, binding) {
    el.__fitTextOpts = binding.value || {};
    schedule(el);
  },
  unbind(el) {
    window.removeEventListener('dashboard-widget-resize', el.__fitTextResize);
    window.removeEventListener('resize', el.__fitTextResize);
    if (el.__fitTextObserver) el.__fitTextObserver.disconnect();
    if (el.__fitTextRaf) cancelAnimationFrame(el.__fitTextRaf);
  },
};
